import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, FileSignature, Printer, Eye } from "lucide-react";
import { toast } from "sonner";

export interface AcceptedTermsRecord {
  id: string;
  quote_number: string | null;
  accepted_at: string | null;
  accepted_by_name: string | null;
  accepted_by_company: string | null;
  accepted_by_email: string | null;
  authority_confirmed: boolean | null;
  standard_terms_version: string | null;
  terms_accepted_text: string | null;
}

const fmtSAST = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-ZA", {
        timeZone: "Africa/Johannesburg",
        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      }) + " SAST"
    : "—";

export function useAcceptedTerms(leadId: string) {
  return useQuery({
    queryKey: ["accepted-terms", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("quotations")
        .select("id, quote_number, accepted_at, accepted_by_name, accepted_by_company, accepted_by_email, authority_confirmed, standard_terms_version, terms_accepted_text")
        .eq("lead_id", leadId)
        .eq("terms_accepted", true)
        .order("accepted_at", { ascending: false });
      if (error) throw error;
      return (data || []) as AcceptedTermsRecord[];
    },
  });
}

function summary(r: AcceptedTermsRecord) {
  return [
    `Terms acceptance record — Quote ${r.quote_number ?? ""}`,
    `Accepted by: ${r.accepted_by_name ?? "—"}${r.accepted_by_company ? ` (${r.accepted_by_company})` : ""}`,
    `Email: ${r.accepted_by_email ?? "—"}`,
    `Accepted on: ${fmtSAST(r.accepted_at)}`,
    `Authorised on behalf of travelling party: ${r.authority_confirmed ? "Yes" : "No"}`,
    r.standard_terms_version ? `Standard terms version: ${r.standard_terms_version}` : "",
  ].filter(Boolean).join("\n");
}

function printRecord(r: AcceptedTermsRecord) {
  const w = window.open("", "_blank");
  if (!w) return toast.error("Please allow pop-ups to print");
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  w.document.write(`<html><head><title>Terms acceptance ${esc(r.quote_number ?? "")}</title></head><body style="font-family:sans-serif;max-width:800px;margin:2rem auto;line-height:1.5"><pre style="white-space:pre-wrap;font-family:inherit">${esc(summary(r))}</pre><hr/><pre style="white-space:pre-wrap;font-family:inherit">${esc(r.terms_accepted_text ?? "")}</pre></body></html>`);
  w.document.close();
  w.print();
}

export function AcceptedTermsDialog({ record, open, onOpenChange }: { record: AcceptedTermsRecord | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  if (!record) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Signed terms — Quote {record.quote_number}</DialogTitle></DialogHeader>
        <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground">{summary(record)}</pre>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border p-3">
          <pre className="whitespace-pre-wrap text-sm font-sans">{record.terms_accepted_text || "No terms text was recorded."}</pre>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${summary(record)}\n\n${record.terms_accepted_text ?? ""}`); toast.success("Copied"); }}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={() => printRecord(record)}>
            <Printer className="h-4 w-4 mr-1" /> Print / PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AcceptedTermsCard({ leadId }: { leadId: string }) {
  const q = useAcceptedTerms(leadId);
  const [open, setOpen] = useState<AcceptedTermsRecord | null>(null);
  const records = q.data || [];
  if (!records.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileSignature className="h-4 w-4" /> Booking terms acceptance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {records.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
            <div className="text-sm">
              <div className="font-medium">
                Accepted by {r.accepted_by_name ?? "—"}{r.accepted_by_company ? ` (${r.accepted_by_company})` : ""}
              </div>
              <div className="text-muted-foreground">
                {r.accepted_by_email} · {fmtSAST(r.accepted_at)} · Quote {r.quote_number}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpen(r)}><Eye className="h-4 w-4 mr-1" /> View full terms</Button>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(summary(r)); toast.success("Summary copied"); }}><Copy className="h-4 w-4 mr-1" /> Copy summary</Button>
              <Button size="sm" variant="outline" onClick={() => printRecord(r)}><Printer className="h-4 w-4 mr-1" /> Print</Button>
            </div>
          </div>
        ))}
      </CardContent>
      <AcceptedTermsDialog record={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} />
    </Card>
  );
}
